import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Package, Trash2, Plus } from 'lucide-react';
import { customers as api } from '../api';
import type { KundenLizenz } from '../api';

/**
 * Paketserver-Zugänge, die dem KUNDEN gehören.
 *
 * WOFÜR: Hyvä ist unser Standard-Frontend für Magento, aber kostenpflichtig —
 * und die Lizenz wird je Shop gekauft. Sie gehört damit dem Verlag, nicht uns.
 * Ein Image mit einer einzigen Firmenlizenz zu bauen und es bei allen Kunden
 * auszurollen wäre eine Lizenzverletzung; deshalb steht der Zugang hier beim
 * Kunden, und sein Image wird mit seinem Zugang gebaut.
 *
 * Allgemein gehalten, weil die kostenpflichtigen Magento-Erweiterungen
 * (Amasty, Mirasvit und die übrigen) genauso funktionieren: ein eigener
 * Composer-Paketserver, ein Zugang je Lizenz.
 *
 * Das Passwort ist genau einmal sichtbar — hier beim Eintippen.
 */

/** Die Paketserver, die wir tatsächlich verwenden. Freie Eingabe bleibt. */
const BEKANNT = [
  { host: 'repo.hyva.io', name: 'Hyvä Theme' },
  { host: 'repo.amasty.com', name: 'Amasty' },
  { host: 'repo.mirasvit.com', name: 'Mirasvit' },
];

export default function LizenzenPanel({ customerId }: { customerId: string }) {
  const qc = useQueryClient();
  const [offen, setOffen] = useState(false);
  const [paketServer, setPaketServer] = useState('repo.hyva.io');
  const [bezeichnung, setBezeichnung] = useState('');
  const [benutzer, setBenutzer] = useState('');
  const [passwort, setPasswort] = useState('');
  const [notiz, setNotiz] = useState('');
  const [fehler, setFehler] = useState('');

  const schluessel = ['customers', customerId, 'lizenzen'];
  const { data, isLoading } = useQuery({
    queryKey: schluessel,
    queryFn: () => api.lizenzen(customerId),
  });

  function fehlerText(vorgabe: string) {
    return (err: unknown) =>
      setFehler(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? vorgabe,
      );
  }

  const anlegen = useMutation({
    mutationFn: () =>
      api.lizenzAnlegen(customerId, { paketServer, bezeichnung, benutzer, passwort, notiz }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: schluessel });
      setOffen(false);
      setBezeichnung('');
      setBenutzer('');
      setPasswort('');
      setNotiz('');
      setFehler('');
    },
    onError: fehlerText('Der Zugang ließ sich nicht speichern.'),
  });

  const loeschen = useMutation({
    mutationFn: (id: string) => api.lizenzLoeschen(customerId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: schluessel }),
    onError: fehlerText('Der Zugang ließ sich nicht entfernen.'),
  });

  const eintraege: KundenLizenz[] = data ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
          <Package size={14} /> Paketserver-Zugänge (Lizenzen des Kunden)
        </h3>
        <button
          onClick={() => setOffen((v) => !v)}
          className="inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-800 font-medium"
        >
          <Plus size={12} /> Zugang hinterlegen
        </button>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Lizenzen, die der Kunde selbst gekauft hat — Hyvä und die
        kostenpflichtigen Magento-Erweiterungen. Sie werden nur beim{' '}
        <strong>Bauen</strong> seines Images verwendet, nie im laufenden Betrieb.
      </p>

      {fehler && <p className="text-xs text-red-600 mb-2">{fehler}</p>}

      {isLoading ? (
        <p className="text-xs text-slate-400">wird geladen …</p>
      ) : eintraege.length === 0 ? (
        <p className="text-xs text-slate-400">
          Nichts hinterlegt. Für einen Magento-Shop mit Hyvä braucht es hier den
          Zugang aus der Lizenz des Kunden.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {eintraege.map((l) => (
            <li
              key={l.id}
              className="flex items-start justify-between gap-3 rounded border border-slate-200 px-2.5 py-1.5"
            >
              <div className="min-w-0">
                <p className="text-xs font-medium text-slate-700 truncate">{l.bezeichnung}</p>
                <p className="text-[11px] text-slate-500 truncate">
                  {l.paketServer} · {l.benutzer}
                  {l.notiz ? ` · ${l.notiz}` : ''}
                </p>
              </div>
              <button
                onClick={() => loeschen.mutate(l.id)}
                title="Zugang entfernen"
                className="text-slate-400 hover:text-red-600 shrink-0"
              >
                <Trash2 size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {offen && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setFehler('');
            anlegen.mutate();
          }}
          className="mt-3 rounded border border-slate-200 bg-slate-50 p-3 space-y-2"
        >
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[11px] text-slate-600">
              Paketserver
              <input
                list="paketserver-bekannt"
                value={paketServer}
                onChange={(e) => setPaketServer(e.target.value)}
                placeholder="repo.hyva.io"
                required
                className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-xs"
              />
              <datalist id="paketserver-bekannt">
                {BEKANNT.map((b) => (
                  <option key={b.host} value={b.host}>
                    {b.name}
                  </option>
                ))}
              </datalist>
            </label>
            <label className="text-[11px] text-slate-600">
              Wofür gilt die Lizenz?
              <input
                value={bezeichnung}
                onChange={(e) => setBezeichnung(e.target.value)}
                placeholder="Hyvä Theme — shop.kunde.de"
                required
                className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-xs"
              />
            </label>
            <label className="text-[11px] text-slate-600">
              Benutzer
              <input
                value={benutzer}
                onChange={(e) => setBenutzer(e.target.value)}
                required
                autoComplete="off"
                className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-xs"
              />
            </label>
            <label className="text-[11px] text-slate-600">
              Passwort
              <input
                type="password"
                value={passwort}
                onChange={(e) => setPasswort(e.target.value)}
                required
                autoComplete="new-password"
                className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-xs"
              />
            </label>
          </div>
          <label className="block text-[11px] text-slate-600">
            Notiz (freiwillig)
            <input
              value={notiz}
              onChange={(e) => setNotiz(e.target.value)}
              placeholder="Lizenznummer, Laufzeit, wer sie gekauft hat"
              className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-xs"
            />
          </label>
          <p className="text-[11px] text-slate-500">
            Eine Hyvä-Lizenz gilt für <strong>einen</strong> Shop. Ist sie beim
            falschen Kunden hinterlegt, wird dessen Image mit einer fremden
            Lizenz gebaut — deshalb die Bezeichnung mit dem Shop, für den sie
            gilt.
          </p>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={anlegen.isPending}
              className="rounded bg-brand-600 px-3 py-1 text-xs text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {anlegen.isPending ? 'wird gespeichert …' : 'Speichern'}
            </button>
            <button
              type="button"
              onClick={() => {
                setOffen(false);
                setFehler('');
              }}
              className="rounded border border-slate-300 px-3 py-1 text-xs text-slate-600 hover:bg-slate-100"
            >
              Abbrechen
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
